<template>
    <div>
        <v-skeleton-loader v-if="loading || fetchingNodePools" elevation="6" type="article@2"></v-skeleton-loader>
        <v-card v-else style="overflow: hidden">
            <v-list two-line color="transparent" class="py-0">
                <v-list-item
                    :to="{
                        name: 'snapshot-overview',
                        params: routeParams()
                    }">
                    <v-list-item-avatar>
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn fab x-small :color="typeButtonColor" elevation="0" v-bind="attrs" v-on="on">
                                    <v-icon small :class="typeIconColorClass">{{ typeIcon }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ typeLabel }}</span>
                        </v-tooltip>
                    </v-list-item-avatar>

                    <v-list-item-content>
                        <v-list-item-title>
                            <b>{{ spaceTitle }}</b>
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-truncate">{{ spaceDescription }}</v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </v-list>

            <v-container v-if="item.instance_name" fluid class="py-0">
                <v-sheet outlined rounded class="px-4 py-2">
                    <v-row no-gutters align="center">
                        <v-col class="d-flex align-center text-truncate">
                            <span class="text--secondary mr-2">Instance:</span>
                            <v-icon color="primary" small class="mr-2">{{ getInstanceTypeIcon(item.instance_name) }}</v-icon>
                            <span class="secondary--text text-truncate">{{ item.instance_name }}</span>
                        </v-col>
                    </v-row>
                </v-sheet>
            </v-container>

            <v-container fluid>
                <v-row class="align-center">
                    <v-col cols="12">
                        <v-card flat color="grey lighten-3" style="overflow: hidden">
                            <v-list-item
                                two-line
                                :to="{
                                    name: 'app-open',
                                    params: {
                                        oid: item.org_id,
                                        sid: item.space_id,
                                        iid: item.instance_id,
                                        snid: item.snid,
                                        aid: item.aid
                                    }
                                }">
                                <v-list-item-avatar>
                                    <img :src="appIcon(item)" />
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title class="mr-1 d-flex align-center text-truncate secondary--text header-font">
                                        <span class="text-truncate">{{ item.long_id }}</span>
                                        <v-chip
                                            v-if="pushUiEnabled && deployInfo.label"
                                            x-small
                                            role="status"
                                            aria-live="polite"
                                            :color="deployInfo.color"
                                            text-color="white"
                                            class="ml-2 flex-shrink-0"
                                            :class="{ 'status-pulse': deployInfo.pulse }">
                                            {{ deployInfo.label }}
                                        </v-chip>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        <span class="mr-3">{{ item.description }}</span>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <!-- Legacy fabs when push UI is off; enhanced state when on. -->
                                    <template v-if="!pushUiEnabled">
                                        <v-btn v-if="!isRunningLegacy && !isStartingLegacy" fab dark x-small color="primary">
                                            <v-icon small>power_settings_new</v-icon>
                                        </v-btn>
                                        <stop-application v-if="isStartingLegacy" :app="item" :appName="item.long_id" :isShared="item.shared === 'true'">
                                            <v-btn fab x-small dark color="deep-orange lighten-2">
                                                <v-progress-circular size="18" color="white" indeterminate />
                                            </v-btn>
                                        </stop-application>
                                        <stop-application v-if="isRunningLegacy" :app="item" :appName="item.long_id" :isShared="item.shared === 'true'">
                                            <v-btn fab x-small dark color="success">
                                                <v-icon small>power_settings_new</v-icon>
                                            </v-btn>
                                        </stop-application>
                                    </template>
                                    <template v-else>
                                        <v-btn v-if="!activeDeployment" fab dark x-small color="primary">
                                            <v-icon small>power_settings_new</v-icon>
                                        </v-btn>
                                        <stop-application
                                            v-else
                                            :app="item"
                                            :appName="item.long_id"
                                            :isShared="item.shared === 'true'"
                                            :sessionId="activeDeployment.session_id">
                                            <v-btn fab x-small dark :color="deployInfo.color">
                                                <v-progress-circular v-if="deployInfo.pulse" size="18" color="white" indeterminate />
                                                <v-icon v-else-if="deployInfo.state === 'UNAVAILABLE'" small>mdi-alert</v-icon>
                                                <v-icon v-else small>power_settings_new</v-icon>
                                            </v-btn>
                                        </stop-application>
                                    </template>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider class="mx-4"></v-divider>
                            <v-card-actions>
                                <v-container fluid>
                                    <v-row>
                                        <v-col class="d-flex align-center py-1">
                                            <span>Size:</span>
                                            <app-scale-info-menu :app="item">
                                                <v-chip label class="ml-2 pl-4">
                                                    <template v-if="item.active_resource === 'ncu'">
                                                        <v-icon small left color="primary">mdi-card-bulleted-outline</v-icon>
                                                        {{ `${item.ncu} CU` }}
                                                    </template>
                                                    <template v-else>
                                                        <v-icon small left color="purple" v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'cpu'">
                                                            mdi-memory
                                                        </v-icon>
                                                        <v-icon
                                                            small
                                                            left
                                                            color="light-green"
                                                            v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'gpu'">
                                                            mdi-memory mdi-rotate-45
                                                        </v-icon>
                                                        <v-icon small left color="orange" v-if="getNodepoolFamily(currentNodePool(item.node_pool)) === 'ssd'">
                                                            mdi-nas
                                                        </v-icon>
                                                        {{ `${getNodepoolPrimaryParameter(currentNodePool(item.node_pool))}` }}
                                                    </template>
                                                </v-chip>
                                            </app-scale-info-menu>
                                        </v-col>
                                        <v-spacer></v-spacer>
                                        <v-col cols="auto" class="d-flex align-center py-1">
                                            <span class="mr-2">Credit/hour:</span>
                                            <span v-if="item.active_resource === 'ncu'">-</span>
                                            <span v-else>
                                                {{ toLocaleFixed(currentNodePool(item.node_pool).credits_per_hour, $appConfig?.VUE_APP_DASHBOARD_PRECISION) }}
                                            </span>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-card>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { appTypeAndImageLink } from '@/mixins/appTypeAndImage'
import { scaling } from '@/mixins/scaling'
import spaceTypeUi from '@/mixins/spaceTypeUi'
import { enumsData } from '@/mixins/enums'
import { pushUiMixin } from '@/mixins/pushUi'
import { getInstanceTypeIcon } from '@/utils'

const StopApplication = () => import('@/modules/application/components/TheApplicationStopDialog.vue')
const AppScaleInfoMenu = () => import('@/components/AppScaleInfoMenu.vue')

export default {
    name: 'DashboardQuickAccessCard',
    components: { StopApplication, AppScaleInfoMenu },
    props: ['item', 'loading'],
    mixins: [spaceTypeUi, appTypeAndImageLink, scaling, enumsData, pushUiMixin],
    methods: {
        getInstanceTypeIcon,
        routeParams() {
            return {
                oid: this.item.org_id,
                sid: this.item.space_id,
                iid: this.item.instance_id,
                snid: this.item.snid
            }
        }
    },
    computed: {
        ...mapState('orgStore', ['researchSpaces', 'educationSpaces', 'datasetSpaces']),
        ...mapState('appStore', ['deploymentStatus']),
        ...mapGetters('appStore', ['activeDeployments', 'runningDeployments', 'startingDeployments']),
        activeDeployment() {
            return this.activeDeployments.find(deployment => String(deployment.aid) === String(this.item?.aid)) || null
        },
        deployInfo() {
            return this.statusInfoFor(this.deploymentStatus || [], this.item?.aid)
        },
        isRunningLegacy() {
            return (this.runningDeployments || []).some(d => String(d.aid) === String(this.item?.aid))
        },
        isStartingLegacy() {
            return (this.startingDeployments || []).some(d => String(d.aid) === String(this.item?.aid))
        },
        allSpaces() {
            return [...this.educationSpaces, ...this.researchSpaces, ...this.datasetSpaces]
        },
        currentSpace() {
            const sid = this.item?.space_id?.toString()
            if (!sid) {
                return null
            }

            return this.allSpaces.find(space => space?.sid?.toString() === sid) || null
        },
        spaceTypeSpace() {
            return { sid: this.item?.space_id }
        },
        spaceTitle() {
            return this.currentSpace?.space_long_id || this.item?.space_name || 'Space'
        },
        spaceDescription() {
            return this.currentSpace?.space_description || ''
        },
        typeButtonColor() {
            return this.spaceTypeButtonColor(this.spaceTypeSpace)
        },
        typeIconColorClass() {
            return this.spaceTypeIconColorClass(this.spaceTypeSpace)
        },
        typeIcon() {
            return this.spaceTypeIcon(this.spaceTypeSpace)
        },
        typeLabel() {
            return this.spaceTypeLabel(this.spaceTypeSpace)
        }
    }
}
</script>

<style scoped>
.status-pulse {
    animation: statusPulse 1.2s ease-in-out infinite;
}
@keyframes statusPulse {
    0%,
    100% {
        opacity: 0.55;
    }
    50% {
        opacity: 1;
    }
}
@media (prefers-reduced-motion: reduce) {
    .status-pulse {
        animation: none;
    }
}
</style>
